import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router';
import { TableCell } from '@mui/material';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';

import { OrderStatusEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import { RootState } from '../../../lib/store';
import {
  getAccountExpirationDate,
  hasAccountOnlyDemoPlan,
} from '../../../lib/store/crm/helpers';
import { mapOrderStatus } from '../../../lib/util/trading-status-helpers';

import { useTimeRemaining } from '../../../hooks/useTimeRemaining';
import { UI_ROUTES } from '../../../util/routes';

import ButtonsCancelAndModify from './ButtonsCancelAndModify/ButtonsCancelAndModify';
import ButtonsSellAndBuy from './ButtonsSellAndBuy/ButtonsSellAndBuy';
import { basic, green, red, warning } from './colors';

import styles from '../TableView.module.scss';

const useStyles = makeStyles(() => ({
  root: {
    '&:nth-last-child(2)': {
      color: (props: any) => {
        let status: string = '';
        if (props.status) {
          status = props.status.toLowerCase();
        }
        switch (status) {
          case OrderStatusEnum.Filled: return green;
          case OrderStatusEnum.Cancelled: return red;
          case OrderStatusEnum.PartiallyFilled: return warning;
          default: return basic;
        }
      },
    },
    borderBottom: 'none',
  },
}));

type Props = ReduxProps & {
  row: any;
  rowKey: string;
  headerKey: string;
  otherButtons?: string;
  showButtons?: boolean;
  setIsSellOrBuySelected?: (value: boolean) => void;
}

const TableRows = (props: Props) => {
  const {
    row,
    rowKey,
    headerKey,
    otherButtons,
    showButtons,
    setIsSellOrBuySelected,
    expirationDate,
    isUseDemoAccountOrHasOnlyDemoAccount,
  } = props;
  const { hasNoRemainingTime } = useTimeRemaining(expirationDate);

  const { pathname } = useLocation();
  const classes = useStyles(row);

  const handleButtons = () => {
    if (isUseDemoAccountOrHasOnlyDemoAccount && hasNoRemainingTime) {
      return null;
    }

    switch (pathname) {
      case UI_ROUTES.dashboard:
      case `${UI_ROUTES.symbol}/${row.name}`:
        if (otherButtons === lang.commonMyAccountButtonMyStocks()) {
          return (
            <div className={styles.wrapperSellAndBuy}>
              <ButtonsSellAndBuy
                row={row}
                showButtons={showButtons}
                setIsSellOrBuySelected={setIsSellOrBuySelected}
              />
            </div>
          );
        }

        return (
          <ButtonsCancelAndModify row={row} showModifyButtons />
        );
      default: return null;
    }
  };

  const handleRowKey = (key: string) => {
    switch (key) {
      case OrderStatusEnum.Filled: return mapOrderStatus(key);
      case OrderStatusEnum.Rejected: return mapOrderStatus(OrderStatusEnum.Cancelled);
      case OrderStatusEnum.PartiallyFilled: return mapOrderStatus(key);
      default: return key;
    }
  };

  return (
    <>
      <TableCell
        className={classNames(styles.tableRow, {
          [styles.unrealizedProfitGreen]: headerKey === 'unrealizedProfit' && row.unrealizedProfitNumber > 0,
          [styles.unrealizedProfitRed]: headerKey === 'unrealizedProfit' && row.unrealizedProfitNumber < 0,
        })}
        align="left"
        classes={{
          root: classes.root,
        }}
      >
        {handleRowKey(rowKey)}
        {headerKey === 'buttons'
          && handleButtons()}
      </TableCell>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isUseDemoAccountOrHasOnlyDemoAccount: state.crm.isUseDemoAccount || hasAccountOnlyDemoPlan(state.crm),
  expirationDate: getAccountExpirationDate(state),
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(TableRows);
